import { CSSProperties, PointerEvent, useEffect, useRef, useState } from 'react';
import { resolveSdkAsset } from '../../core/env';

const IDLE_MS = 30_000;
const DRAG_THRESHOLD_PX = 5;
const FADED_OPACITY = 0.3;
/** Cao hơn modal / welcome popup (10000). */
const Z_INDEX = 10050;

type Variant = 'ngang' | 'tron';

const VARIANTS: Record<Variant, { src: string; width: number; height: number }> = {
  ngang: {
    src: resolveSdkAsset('/18/18_ngang.png'),
    width: 148,
    height: 60,
  },
  tron: {
    src: resolveSdkAsset('/18/18_tron.png'),
    width: 52,
    height: 52,
  },
};

function clampPosition(left: number, top: number, width: number, height: number) {
  const maxLeft = Math.max(0, window.innerWidth - width);
  const maxTop = Math.max(0, window.innerHeight - height);
  return {
    left: Math.min(Math.max(0, left), maxLeft),
    top: Math.min(Math.max(0, top), maxTop),
  };
}

export default function AgeRatingBadge({ onTap }: { onTap?: () => void } = {}) {
  const [variant, setVariant] = useState<Variant>('ngang');
  const [faded, setFaded] = useState(false);
  const [dragging, setDragging] = useState(false);
  /** null = dùng top/right %; sau lần kéo đầu chuyển sang left/top px. */
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const elRef = useRef<HTMLButtonElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    origLeft: 0,
    origTop: 0,
    pointerId: -1,
  });
  const onTapRef = useRef(onTap);
  onTapRef.current = onTap;

  const resetIdle = () => {
    setFaded(false);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = setTimeout(() => setFaded(true), IDLE_MS);
  };

  useEffect(() => {
    resetIdle();
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pos) return;
    const { width, height } = VARIANTS[variant];
    setPos((prev) => (prev ? clampPosition(prev.left, prev.top, width, height) : prev));
  }, [variant]); // eslint-disable-line react-hooks/exhaustive-deps -- chỉ reclamp khi đổi icon

  const readRect = () => {
    const el = elRef.current;
    if (!el) return { left: 0, top: 0 };
    const rect = el.getBoundingClientRect();
    return { left: rect.left, top: rect.top };
  };

  const applyDragTransform = (dx: number, dy: number) => {
    const el = elRef.current;
    if (!el) return;
    el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
  };

  const clearDragTransform = () => {
    const el = elRef.current;
    if (!el) return;
    el.style.transform = 'translate3d(0,0,0)';
  };

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { left, top } = readRect();
    // Chốt vị trí px ngay khi bắt đầu kéo (tránh nhảy từ right% → left)
    setPos({ left, top });
    clearDragTransform();
    dragRef.current = {
      active: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      origLeft: left,
      origTop: top,
      pointerId: e.pointerId,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    resetIdle();
  };

  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag.active) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (
      !drag.moved &&
      (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)
    ) {
      drag.moved = true;
      setDragging(true);
    }
    if (!drag.moved) return;

    const { width, height } = VARIANTS[variant];
    const next = clampPosition(drag.origLeft + dx, drag.origTop + dy, width, height);
    // Chỉ dịch bằng transform (GPU) — không setState left/top mỗi frame → hết vệt ghost
    applyDragTransform(next.left - drag.origLeft, next.top - drag.origTop);
  };

  const endPointer = (e: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag.active) return;
    drag.active = false;
    setDragging(false);

    try {
      e.currentTarget.releasePointerCapture(drag.pointerId);
    } catch {
      /* ignore */
    }

    if (drag.moved) {
      const el = elRef.current;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const { width, height } = VARIANTS[variant];
      const next = clampPosition(drag.origLeft + dx, drag.origTop + dy, width, height);
      clearDragTransform();
      setPos(next);
      // Sync transform clear trước paint tiếp theo nếu el vẫn giữ offset
      if (el) el.style.transform = 'translate3d(0,0,0)';
    } else {
      clearDragTransform();
      setVariant((prev) => (prev === 'ngang' ? 'tron' : 'ngang'));
      // Tap (không kéo) — mở lại verify OTP nếu callback có
      onTapRef.current?.();
    }
    resetIdle();
  };

  const asset = VARIANTS[variant];
  const style: CSSProperties = {
    position: 'fixed',
    zIndex: Z_INDEX,
    width: asset.width,
    height: asset.height,
    opacity: faded ? FADED_OPACITY : 1,
    top: pos ? pos.top : '10%',
    right: pos ? 'auto' : '40%',
    left: pos ? pos.left : 'auto',
    transform: 'translate3d(0,0,0)',
    willChange: dragging ? 'transform' : 'opacity',
    transition: dragging ? 'none' : undefined,
  };

  const imgStyle: CSSProperties = {
    width: asset.width,
    height: asset.height,
    display: 'block',
    objectFit: 'contain',
  };

  return (
    <button
      ref={elRef}
      type="button"
      className={`age-rating-badge${dragging ? ' is-dragging' : ''}`}
      aria-label="Phân loại độ tuổi 18+"
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
    >
      <img
        src={asset.src}
        width={asset.width}
        height={asset.height}
        alt="18+"
        draggable={false}
        style={imgStyle}
      />
    </button>
  );
}
